import React from "react";
import { Link } from "react-router-dom";
import { Check, Monitor, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";

/**
 * PlanCard renders a single pricing tier for a product.
 * Used on the home page featured section (3 Huntheal PC plans).
 */
export const PlanCard = ({ product, plan, highlighted = false, features = [] }) => {
  const { addItem } = useCart();
  const off = plan.original
    ? Math.round((1 - plan.price / plan.original) * 100)
    : 0;

  return (
    <article
      data-testid={`plan-card-${plan.id}`}
      className={`relative flex h-full flex-col border bg-white p-6 transition-all duration-200 hover:border-slate-900 hover:shadow-[8px_8px_0_0_rgba(15,23,42,1)] ${
        highlighted ? "border-2 border-slate-900" : "border-slate-200"
      }`}
    >
      {highlighted && (
        <div className="absolute -top-3 left-6 bg-emerald-500 px-3 py-1 text-[10px] font-black uppercase tracking-[0.2em] text-white">
          Most Popular
        </div>
      )}

      <div className="flex items-start justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600">
            {product.name}
          </div>
          <h3 className="mt-2 font-display text-2xl font-bold tracking-tight text-slate-900">
            {plan.label}
          </h3>
        </div>
        <div className="flex items-center gap-1 border border-slate-200 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-600">
          <Monitor className="h-3 w-3" />
          {plan.devices} {plan.devices === 1 ? "PC" : "PCs"}
        </div>
      </div>

      <div className="mt-4 flex items-end gap-2">
        <div className="font-display text-4xl font-black tracking-tighter text-slate-900">
          ${plan.price.toFixed(2)}
        </div>
        {plan.original && (
          <div className="pb-1 text-xs text-slate-500">
            <span className="line-through">${plan.original.toFixed(2)}</span>
            {off > 0 && <span className="ml-1 font-bold text-emerald-600">-{off}%</span>}
          </div>
        )}
      </div>
      <div className="text-xs text-slate-500">First year. Auto-renews after.</div>

      <ul className="mt-5 space-y-2 text-sm text-slate-700">
        {features.slice(0, 5).map((f) => (
          <li key={f} className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" strokeWidth={3} />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto flex gap-2 pt-6">
        <Button
          data-testid={`plan-add-${plan.id}`}
          onClick={() => addItem(product.id, plan.id, 1)}
          className="flex-1 rounded-sm bg-slate-900 text-white hover:bg-slate-800"
        >
          Add to Cart
        </Button>
        <Button
          asChild
          variant="outline"
          data-testid={`plan-view-${plan.id}`}
          className="rounded-sm border-slate-300 hover:border-slate-900"
        >
          <Link to={`/products/${product.id}`}>
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>
    </article>
  );
};
